import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input,
  Select,
  Segmented,
  Tag,
  Progress,
  Typography,
  Avatar,
} from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  EyeOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  UserOutlined,
  WarningFilled,
  CheckCircleFilled,
  NodeIndexOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { actionItemApi, ActionItem, SubTask } from '@/api/actionItemApi';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import dayjs from 'dayjs';

const { Text } = Typography;

type ViewMode = 'By gate' | 'By status' | 'Pipeline';

interface ColumnDef {
  key: string;
  title: string;
  color: string;
  dotColor: string;
}

// Gate colours mirror getPhaseColor() in utils/phaseHelpers — one distinct
// hue per gate, matching the pipeline reference design. dotColor is lighter.
const GATE_COLUMNS: ColumnDef[] = [
  { key: 'PHASE_A', title: 'Gate A', color: '#8B5CF6', dotColor: '#A78BFA' }, // purple
  { key: 'PHASE_B', title: 'Gate B', color: '#3B82F6', dotColor: '#60A5FA' }, // blue
  { key: 'PHASE_C', title: 'Gate C', color: '#06B6D4', dotColor: '#22D3EE' }, // cyan
  { key: 'PHASE_D', title: 'Gate D', color: '#10B981', dotColor: '#34D399' }, // green
  { key: 'PHASE_E', title: 'Gate E', color: '#F59E0B', dotColor: '#FBBF24' }, // amber
  { key: 'PHASE_F', title: 'Gate F', color: '#EC4899', dotColor: '#F472B6' }, // pink
];

const GATE_SUBTITLES: Record<string, string> = {
  PHASE_A: 'Discovery',
  PHASE_B: 'Go / No-Go',
  PHASE_C: 'Resource Availability',
  PHASE_D: 'Technical Solution',
  PHASE_E: 'Review',
  PHASE_F: 'Cost Sheet & Submission',
};

const STATUS_COLUMNS: ColumnDef[] = [
  { key: 'todo', title: 'TO DO', color: '#64748B', dotColor: '#94A3B8' },
  { key: 'in_progress', title: 'IN PROGRESS', color: '#2563EB', dotColor: '#3B82F6' },
  { key: 'in_review', title: 'IN REVIEW', color: '#D97706', dotColor: '#F59E0B' },
  { key: 'done', title: 'DONE', color: '#16A34A', dotColor: '#22C55E' },
];

function classifyItem(item: ActionItem): string {
  const unmet = item.unmetRequirements?.length ?? 0;
  const total = item.totalRequirements ?? 0;
  if (unmet === 0) return 'done';
  if (item.currentPhase === 'PHASE_E') return 'in_review';
  if (unmet < total) return 'in_progress';
  return 'todo';
}

function getProgressPercent(item: ActionItem): number {
  const total = item.totalRequirements || 1;
  const met = total - (item.unmetRequirements?.length ?? 0);
  return Math.round((met / total) * 100);
}

function getProgressColor(pct: number): string {
  if (pct >= 80) return '#16A34A';
  if (pct >= 60) return '#D97706';
  return '#DC2626';
}

function formatDeadline(item: ActionItem): string {
  if (!item.phaseDeadline) return '';
  const d = dayjs(item.phaseDeadline);
  const today = dayjs().startOf('day');
  if (d.isSame(today, 'day')) return 'Today';
  if (d.isSame(today.add(1, 'day'), 'day')) return 'Tomorrow';
  return d.format('MMM D');
}

function formatRoleName(role?: string): string {
  if (!role) return '';
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Collect all individual person names from an item (owner + sub-task assignees) */
function getAllPeople(item: ActionItem): string[] {
  const names: string[] = [];
  if (item.assigneeName) names.push(item.assigneeName);
  item.subTasks?.forEach((st) => {
    if (st.assigneeName && !names.includes(st.assigneeName)) {
      names.push(st.assigneeName);
    }
  });
  return names;
}

/** Check if a person is involved in this item (as owner or sub-task assignee) */
function isPersonInvolved(item: ActionItem, name: string): boolean {
  if (item.assigneeName === name) return true;
  return item.subTasks?.some((st) => st.assigneeName === name) ?? false;
}

const SUB_TASK_STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  Submitted: { bg: '#DCFCE7', text: '#166534', dot: '#16A34A' },
  Approved: { bg: '#DCFCE7', text: '#166534', dot: '#16A34A' },
  Pending: { bg: '#FEF3C7', text: '#92400E', dot: '#D97706' },
  In_progress: { bg: '#DBEAFE', text: '#1E40AF', dot: '#2563EB' },
};

function getSubTaskColor(status: string) {
  return SUB_TASK_STATUS_COLORS[status] || SUB_TASK_STATUS_COLORS['Pending'];
}

/* ─── Stat Card ─── */
function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div
      className="ui-surface"
      style={{
        flex: '1 1 0',
        minWidth: 150,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          background: color + '14',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          fontSize: 17,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{label}</div>
      </div>
    </div>
  );
}

/* ─── Sub Task Row ─── */
function SubTaskRow({ st }: { st: SubTask }) {
  const c = getSubTaskColor(st.status);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 0',
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: c.dot,
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          fontSize: 10,
          color: 'var(--color-text-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {st.name}
      </div>
      {st.assigneeName && (
        <span
          style={{
            fontSize: 9,
            color: '#6366F1',
            background: '#EEF2FF',
            padding: '1px 5px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
            maxWidth: 80,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {st.assigneeName.split(' ')[0]}
        </span>
      )}
      <Tag
        style={{
          fontSize: 9,
          lineHeight: '14px',
          margin: 0,
          borderRadius: 4,
          padding: '0 4px',
          background: c.bg,
          border: 'none',
          color: c.text,
        }}
      >
        {st.status}
      </Tag>
    </div>
  );
}

/* ─── Task Card ─── */
function TaskCard({ item, onClick }: { item: ActionItem; onClick: () => void }) {
  const pct = getProgressPercent(item);
  const deadline = formatDeadline(item);
  const isOverdue = item.phaseDeadlineOverdue === true;
  const initials = item.assigneeName
    ? item.assigneeName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '';
  const subTasks = item.subTasks ?? [];

  return (
    <div
      onClick={onClick}
      className="ui-surface ui-surface--interactive"
      style={{
        borderRadius: 12,
        padding: 12,
        marginBottom: 8,
        borderLeft: `3px solid ${isOverdue ? '#DC2626' : '#16A34A'}`,
      }}
    >
      {/* Overdue / On Time badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        {isOverdue ? (
          <Tag
            icon={<WarningFilled />}
            color="error"
            style={{ fontSize: 10, lineHeight: '18px', margin: 0, borderRadius: 4 }}
          >
            Overdue
          </Tag>
        ) : item.phaseDeadline ? (
          <Tag
            icon={<CheckCircleFilled />}
            color="success"
            style={{ fontSize: 10, lineHeight: '18px', margin: 0, borderRadius: 4 }}
          >
            On Time
          </Tag>
        ) : (
          <Tag
            style={{
              fontSize: 10,
              lineHeight: '18px',
              margin: 0,
              borderRadius: 4,
              background: 'var(--color-bg-hover)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            No Deadline
          </Tag>
        )}
        {deadline && (
          <Text
            style={{
              fontSize: 10,
              color: isOverdue ? 'var(--color-error)' : 'var(--color-text-secondary)',
              fontWeight: isOverdue ? 600 : 400,
            }}
          >
            {deadline}
          </Text>
        )}
      </div>

      {/* Title */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          marginBottom: 2,
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {item.title || 'Untitled Opportunity'}
      </div>

      {/* Agency */}
      {item.agencyName && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--color-text-secondary)',
            marginBottom: 8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.agencyName}
        </div>
      )}

      {/* Progress bar */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <Text style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>Gate readiness</Text>
          <Text style={{ fontSize: 10, fontWeight: 600, color: getProgressColor(pct) }}>
            {pct}%
          </Text>
        </div>
        <Progress
          percent={pct}
          showInfo={false}
          size="small"
          strokeColor={getProgressColor(pct)}
          trailColor="#F1F5F9"
          style={{ margin: 0 }}
        />
      </div>

      {/* Sub-tasks */}
      {subTasks.length > 0 && (
        <div
          style={{
            marginBottom: 8,
            padding: '6px 8px',
            background: 'var(--color-bg-container)',
            borderRadius: 6,
            border: '1px solid var(--color-border-light)',
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
            Sub-tasks ({subTasks.filter((s) => s.status === 'Submitted' || s.status === 'Approved').length}/{subTasks.length})
          </div>
          {subTasks.map((st, i) => (
            <SubTaskRow key={i} st={st} />
          ))}
        </div>
      )}

      {/* Responsible person */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 0 0',
          borderTop: '1px solid var(--color-border-light)',
        }}
      >
        <Avatar
          size={22}
          style={{
            background: item.assigneeName ? '#EEF2FF' : 'var(--color-bg-hover)',
            color: item.assigneeName ? '#4338CA' : 'var(--color-text-tertiary)',
            fontSize: 10,
            fontWeight: 600,
          }}
          icon={!item.assigneeName ? <UserOutlined /> : undefined}
        >
          {initials}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--color-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.assigneeName || 'Unassigned'}
          </div>
          {item.assigneeRole && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--color-text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatRoleName(item.assigneeRole)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Column ─── */
function KanbanColumn({
  column,
  subtitle,
  items,
  onCardClick,
}: {
  column: ColumnDef;
  subtitle?: string;
  items: ActionItem[];
  onCardClick: (id: string) => void;
}) {
  return (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 230,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          marginBottom: 10,
          padding: '10px 12px',
          background: column.color + '0A',
          borderRadius: 12,
          borderTop: `3px solid ${column.dotColor}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: column.dotColor,
              flexShrink: 0,
            }}
          />
          <Text strong style={{ fontSize: 13, color: column.color, letterSpacing: '0.01em' }}>
            {column.title}
          </Text>
          <span
            style={{
              marginLeft: 'auto',
              minWidth: 22,
              height: 20,
              padding: '0 7px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 999,
              background: column.color + '14',
              color: column.color,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {items.length}
          </span>
        </div>
        {subtitle && (
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{subtitle}</div>
        )}
      </div>

      <div
        className="ui-surface"
        style={{
          flex: 1,
          background: 'var(--color-bg-container)',
          borderRadius: 14,
          padding: 8,
          minHeight: 160,
          maxHeight: 'calc(100vh - 340px)',
          overflowY: 'auto',
        }}
      >
        {items.length === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 80,
              color: 'var(--color-text-disabled)',
              fontSize: 12,
            }}
          >
            No opportunities
          </div>
        ) : (
          <div className="ui-stagger">
            {items.map((item) => (
              <TaskCard
                key={item.opportunityId + item.currentPhase}
                item={item}
                onClick={() => onCardClick(item.opportunityId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Pipeline view (ported from rfo-portal) ─── */

// "3d overdue" / "Due today" / "Due in 5d" from the item's phase-deadline fields.
function pipelineDueText(item: ActionItem): string {
  const days = item.daysUntilPhaseDeadline;
  if (typeof days === 'number') {
    if (item.phaseDeadlineOverdue || days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return 'Due today';
    return `Due in ${days}d`;
  }
  return item.phaseDeadlineOverdue ? 'Overdue' : '';
}

function PipelineCard({ item, color, onClick }: { item: ActionItem; color: string; onClick: () => void }) {
  const dueText = pipelineDueText(item);
  const severity = item.phaseDeadlineOverdue
    ? 'overdue'
    : typeof item.daysUntilPhaseDeadline === 'number' && item.daysUntilPhaseDeadline <= 7
      ? 'soon'
      : 'ok';
  const severityColor =
    severity === 'overdue'
      ? 'var(--color-error)'
      : severity === 'soon'
        ? 'var(--color-warning)'
        : 'var(--color-success)';
  const SeverityIcon =
    severity === 'overdue' ? WarningFilled : severity === 'soon' ? WarningOutlined : ClockCircleOutlined;

  return (
    <div
      onClick={onClick}
      className="ui-surface ui-surface--interactive"
      style={{
        flexShrink: 0,
        width: 230,
        borderRadius: 10,
        borderTop: `4px solid ${color}`,
        padding: '12px 14px',
      }}
    >
      <div
        title={item.title}
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          marginBottom: 6,
        }}
      >
        {item.title || 'Untitled Opportunity'}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: 'var(--color-text-tertiary)',
          marginBottom: 8,
        }}
      >
        <ClockCircleOutlined style={{ fontSize: 11 }} />
        {item.phaseDeadline ? dayjs(item.phaseDeadline).format('MMM D') : '—'}
      </div>
      {dueText && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 600,
            color: severityColor,
          }}
        >
          <SeverityIcon style={{ fontSize: 12 }} /> {dueText}
        </span>
      )}
    </div>
  );
}

function PipelineRow({
  column,
  subtitle,
  items,
  onCardClick,
}: {
  column: ColumnDef;
  subtitle?: string;
  items: ActionItem[];
  onCardClick: (id: string) => void;
}) {
  return (
    <div
      className="ui-surface"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 14,
        overflow: 'hidden',
        marginBottom: 12,
      }}
    >
      {/* Left label */}
      <div
        style={{
          flexShrink: 0,
          width: 170,
          borderLeft: `4px solid ${column.color}`,
          padding: '18px 16px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text strong style={{ fontSize: 13 }}>{column.title}</Text>
          <span
            style={{
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 999,
              background: items.length ? column.color : 'var(--color-bg-muted)',
              color: items.length ? '#fff' : 'var(--color-text-tertiary)',
            }}
          >
            {items.length}
          </span>
        </div>
        {subtitle && (
          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', lineHeight: 1.3 }}>
            {subtitle}
          </span>
        )}
      </div>

      {/* Cards */}
      <div style={{ flex: 1, minWidth: 0, padding: '14px 16px' }}>
        {items.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              fontSize: 12,
              color: 'var(--color-text-disabled)',
            }}
          >
            No opportunities
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4 }}>
            {items.map((item) => (
              <PipelineCard
                key={item.opportunityId + item.currentPhase}
                item={item}
                color={column.color}
                onClick={() => onCardClick(item.opportunityId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ Main Page ═══ */
export default function TaskBoardPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [oppFilter, setOppFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('Pipeline');

  useEffect(() => {
    actionItemApi
      .getBoard()
      .then(setItems)
      .catch((err) => {
        console.error('Failed to load board:', err);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Build filter options
  const oppOptions = useMemo(() => {
    const set = new Set(items.map((i) => i.title).filter(Boolean));
    return [
      { value: 'all', label: 'All opportunities' },
      ...Array.from(set)
        .sort()
        .map((t) => ({ value: t, label: t })),
    ];
  }, [items]);

  // Collect ALL individual people from owners + sub-task assignees
  const assigneeOptions = useMemo(() => {
    const nameSet = new Set<string>();
    items.forEach((item) => {
      getAllPeople(item).forEach((n) => nameSet.add(n));
    });
    return [
      { value: 'all', label: 'All assignees' },
      ...Array.from(nameSet)
        .sort()
        .map((n) => ({ value: n, label: n })),
    ];
  }, [items]);

  // Apply filters — assignee filter matches owner OR sub-task assignees
  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !item.title?.toLowerCase().includes(q) &&
          !item.agencyName?.toLowerCase().includes(q)
        )
          return false;
      }
      if (oppFilter !== 'all' && item.title !== oppFilter) return false;
      if (assigneeFilter !== 'all' && !isPersonInvolved(item, assigneeFilter)) return false;
      return true;
    });
  }, [items, search, oppFilter, assigneeFilter]);

  // Stats
  const stats = useMemo(() => {
    const overdueCount = filtered.filter((i) => i.phaseDeadlineOverdue).length;
    const onTimeCount = filtered.filter(
      (i) => i.phaseDeadline && !i.phaseDeadlineOverdue
    ).length;
    return {
      total: filtered.length,
      overdue: overdueCount,
      onTime: onTimeCount,
      noDeadline: filtered.length - overdueCount - onTimeCount,
    };
  }, [filtered]);

  // Group by gate/phase
  const gateGroups = useMemo(() => {
    const map: Record<string, ActionItem[]> = {};
    GATE_COLUMNS.forEach((col) => {
      map[col.key] = [];
    });
    filtered.forEach((item) => {
      const key = item.currentPhase;
      if (map[key]) map[key].push(item);
    });
    return map;
  }, [filtered]);

  // Group by status
  const statusGroups = useMemo(() => {
    const groups: Record<string, ActionItem[]> = {
      todo: [],
      in_progress: [],
      in_review: [],
      done: [],
    };
    filtered.forEach((item) => {
      const col = classifyItem(item);
      groups[col].push(item);
    });
    return groups;
  }, [filtered]);

  const handleCardClick = (oppId: string) => {
    navigate(`/opportunities/${oppId}`);
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Pipeline Board" subtitle="Loading…" />
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="ui-surface"
              style={{ flex: '1 1 0', minWidth: 150, height: 70, padding: 16 }}
            >
              <div className="ui-skeleton" style={{ width: 40, height: 40, borderRadius: 12 }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ flex: '1 1 0', minWidth: 230 }}>
              <div className="ui-skeleton" style={{ height: 44, borderRadius: 12, marginBottom: 10 }} />
              <div className="ui-surface" style={{ padding: 8, background: 'var(--color-bg-container)' }}>
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="ui-skeleton" style={{ height: 96, borderRadius: 12, marginBottom: 8 }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Pipeline Board"
        subtitle={`${stats.total} opportunities · ${stats.overdue} overdue · ${stats.onTime} on time`}
      />

      <div className="ui-stagger" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Total Opportunities" value={stats.total} icon={<UnorderedListOutlined />} color="#2563EB" />
        <StatCard label="Overdue" value={stats.overdue} icon={<ClockCircleOutlined />} color="#DC2626" />
        <StatCard label="On Time" value={stats.onTime} icon={<SyncOutlined />} color="#16A34A" />
        <StatCard label="No Deadline Set" value={stats.noDeadline} icon={<EyeOutlined />} color="#94A3B8" />
      </div>

      <div className="ui-toolbar">
        <Input
          placeholder="Search by title or agency..."
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          value={oppFilter}
          onChange={setOppFilter}
          options={oppOptions}
          style={{ minWidth: 200 }}
          showSearch
          optionFilterProp="label"
        />
        <Select
          value={assigneeFilter}
          onChange={setAssigneeFilter}
          options={assigneeOptions}
          style={{ minWidth: 170 }}
          showSearch
          optionFilterProp="label"
        />
        <div style={{ marginLeft: 'auto' }}>
          <Segmented
            options={[
              { value: 'By gate', label: 'By Gate', icon: <AppstoreOutlined /> },
              { value: 'By status', label: 'By Status', icon: <UnorderedListOutlined /> },
              { value: 'Pipeline', label: 'Pipeline', icon: <NodeIndexOutlined /> },
            ]}
            value={viewMode}
            onChange={(val) => setViewMode(val as ViewMode)}
          />
        </div>
      </div>

      {filtered.length === 0 && items.length === 0 ? (
        <div className="ui-surface">
          <EmptyState
            icon={<AppstoreOutlined />}
            title="No opportunities yet"
            description="Opportunities will appear on the board as they move through the gates."
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ui-surface">
          <EmptyState
            icon={<SearchOutlined />}
            title="No matching opportunities"
            description="Try a different search term or clearing the filters above."
          />
        </div>
      ) : viewMode === 'By gate' ? (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {GATE_COLUMNS.map((col) => (
            <KanbanColumn
              key={col.key}
              column={col}
              subtitle={GATE_SUBTITLES[col.key]}
              items={gateGroups[col.key] || []}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      ) : viewMode === 'Pipeline' ? (
        <div className="ui-stagger">
          {GATE_COLUMNS.map((col) => (
            <PipelineRow
              key={col.key}
              column={col}
              subtitle={GATE_SUBTITLES[col.key]}
              items={gateGroups[col.key] || []}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {STATUS_COLUMNS.map((col) => (
            <KanbanColumn
              key={col.key}
              column={col}
              items={statusGroups[col.key]}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
